import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { clearTokens, getCurrentUserProfile, getProfilePictureUrl } from '../services/api';
import api, { formatApiError, checkImputationDuplicate, getWorkflows, getTenantSubdomain } from '../services/api';
import { getImputationEtatOptions, getDefaultEtatForNewImputation } from '../utils/imputationEtats';
import AdminTopBar from './AdminTopBar';
import Toast from './Toast';
import useToast from '../hooks/useToast';

// Preferred workflow order for display (GCR first, BYNOW last) — used for multi-insert default
const WORKFLOW_DISPLAY_ORDER = ['GCR', 'GIR', 'PROMO', 'BYNOW'];
const sortWorkflowCodes = (codes) => {
  if (!codes || !codes.length) return codes || [];
  return [...codes].sort((a, b) => {
    const ia = WORKFLOW_DISPLAY_ORDER.indexOf(a);
    const ib = WORKFLOW_DISPLAY_ORDER.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return (a || '').localeCompare(b || '');
  });
};

const CreateImputationPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  
  // Current user data state
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  // State for operations
  const [operations, setOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const allWorkflowCodes = sortWorkflowCodes(workflows.map(w => w.code).filter(Boolean));

  // State for form data
  const [formData, setFormData] = useState({
    date_operation: new Date().toISOString().split('T')[0],
    gcr_prefix: 'GCR',
    gcr_code: '',
    operation: '',
    etat_final: getDefaultEtatForNewImputation(getTenantSubdomain()),
    commentaire: '',
    quantity: 1
  });

  // State for domain filter (filters Operation Code options)
  const [selectedDomain, setSelectedDomain] = useState('');
  // State for production line filter (filters Operation Code options)
  const [selectedProductionLine, setSelectedProductionLine] = useState('');

  // State for operation search
  const [operationSearch, setOperationSearch] = useState('');
  const [showOperationDropdown, setShowOperationDropdown] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);

  // State for notifications
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { toast, showSuccess, showWarning, showError, showInfo, hideToast } = useToast();

  // Duplicate project code: confirmation modal and pending submit
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  // When quantity > 1: one Workflow + Project Code per imputation
  const [projectCodeEntries, setProjectCodeEntries] = useState([{ gcr_prefix: 'GCR', gcr_code: '' }]);

  // Refs
  const operationRef = useRef(null);

  // Project code: type-specific prefix and max digits (loaded from workflows)
  const projectCodePrefixByType = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = m[w.code] || (w.project_code || '').trim(); });
    return Object.keys(m).length ? m : { GCR: 'INT-0000', GIR: 'TI000000', PROMO: 'T', BYNOW: 'INC' };
  }, [workflows]);
  const projectCodeMaxLengthByType = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = Math.max(0, Math.min(20, parseInt(w.digits_in_project_code, 10) || 0)); });
    return Object.keys(m).length ? m : { GCR: 7, GIR: 7, PROMO: 5, BYNOW: 10 };
  }, [workflows]);
  const workflowLabelByPrefix = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = (w.label || w.code).trim(); });
    return Object.keys(m).length ? m : { GCR: 'GCR', GIR: 'TT GIR', PROMO: 'PROMO', BYNOW: 'BYNOW' };
  }, [workflows]);
  const allowAlphabeticByPrefix = React.useMemo(() => {
    const m = {};
    workflows.forEach(w => { if (w.code) m[w.code] = w.allow_alphabetic_chars !== false; });
    return Object.keys(m).length ? m : { GCR: true, GIR: true, PROMO: true, BYNOW: true };
  }, [workflows]);
  const getProjectCodeMaxLength = (prefix) => projectCodeMaxLengthByType[prefix] ?? 7;
  const getZeroProjectCode = (prefix) => '0'.repeat(getProjectCodeMaxLength(prefix));
  const isProjectCodeOptionalForPrefix = (prefix) => getProjectCodeMaxLength(prefix) === 0;

  const etatChoices = React.useMemo(() => getImputationEtatOptions(getTenantSubdomain()), []);

  // Unique domains from operations (for Domain dropdown)
  const availableDomains = React.useMemo(() => {
    const domainSet = new Set();
    operations.forEach(op => {
      if (op.domain && String(op.domain).trim()) domainSet.add(op.domain.trim());
    });
    return Array.from(domainSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [operations]);

  // Operations filtered by selected domain (MULTI-DOMAINE appears for any domain)
  const operationsFilteredByDomain = React.useMemo(() => {
    if (!selectedDomain) return operations;
    return operations.filter(op => {
      const d = op.domain ? String(op.domain).trim() : '';
      if (!d) return false;
      if (d.toUpperCase().includes('MULTI-DOMAINE')) return true;
      return d === selectedDomain;
    });
  }, [operations, selectedDomain]);

  // Unique production lines from domain-filtered operations (for PL filter)
  const availableProductionLines = React.useMemo(() => {
    const plSet = new Set();
    operationsFilteredByDomain.forEach(op => {
      if (op.production_line_lp && String(op.production_line_lp).trim()) plSet.add(op.production_line_lp.trim());
    });
    return Array.from(plSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [operationsFilteredByDomain]);

  // Operations filtered by domain + production line
  const operationsFilteredByDomainAndPL = React.useMemo(() => {
    if (!selectedProductionLine) return operationsFilteredByDomain;
    return operationsFilteredByDomain.filter(op =>
      op.production_line_lp && String(op.production_line_lp).trim() === selectedProductionLine
    );
  }, [operationsFilteredByDomain, selectedProductionLine]);

  // Filter operations based on search; MULTI-DOMAINE operations always appear (not filtered by search)
  const getFilteredOperations = (searchTerm) => {
    const baseOps = operationsFilteredByDomainAndPL;
    if (!searchTerm.trim()) return baseOps;
    const term = searchTerm.toLowerCase();
    const matching = baseOps.filter(op =>
      op.production_line_lp?.toLowerCase().includes(term) || op.activity?.toLowerCase().includes(term)
    );
    const multiDomaine = baseOps.filter(op =>
      op.domain && String(op.domain).toUpperCase().includes('MULTI-DOMAINE')
    );
    const seen = new Set();
    return [...matching, ...multiDomaine].filter(op => {
      if (seen.has(op.id)) return false;
      seen.add(op.id);
      return true;
    });
  };

  // Fetch operations
  const fetchOperations = async () => {
    setLoadingOperations(true);
    try {
      const response = await api.getOperations({ status: 'active' }, 1, 1000);
      setOperations(response.results || []);
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoadingOperations(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (operationRef.current && !operationRef.current.contains(event.target)) {
        setShowOperationDropdown(false);
      }
    };

    if (showOperationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOperationDropdown]);

  // Fetch operations and workflows on mount
  useEffect(() => {
    fetchOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const data = await getWorkflows();
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        setWorkflows(list);
      } catch {
        setWorkflows([]);
      }
    };
    loadWorkflows();
  }, []);

  // Workflows allowed for operation: if operation has workflows assigned, show those; else all workflows from DB
  const allowedWorkflows = selectedOperation?.allowed_workflow_codes;
  const hasWorkflowsAssigned = allowedWorkflows && allowedWorkflows.length > 0;
  const workflowOptions = sortWorkflowCodes(hasWorkflowsAssigned ? allowedWorkflows : (allWorkflowCodes.length ? allWorkflowCodes : ['GCR', 'GIR', 'PROMO', 'BYNOW']));

  // Lock workflow to operation default when operation has predefined workflow
  const isProjectTypeLocked = selectedOperation?.imputation_project_type_choice === 'PREDEFINED' && selectedOperation?.default_project_type;
  const firstWorkflowCode = allWorkflowCodes[0] || 'GCR';
  const lockedProjectType = selectedOperation?.default_project_type || firstWorkflowCode;

  // When reference is disabled for this operation, employees cannot set project/workflow (backend will accept gcr "N/A")
  const isReferenceDisabledForEmployee = currentUser?.role === 'EMPLOYE' && selectedOperation?.is_reference_enabled === false;
  // When no workflow is set for the operation, hide workflow and project code (backend will accept gcr "N/A")
  const shouldHideWorkflowAndProjectCode = isReferenceDisabledForEmployee || (selectedOperation && !hasWorkflowsAssigned);

  useEffect(() => {
    if (isProjectTypeLocked) {
      setFormData(prev => ({ ...prev, gcr_prefix: lockedProjectType, gcr_code: '' }));
      setProjectCodeEntries(prev => prev.map(e => ({ ...e, gcr_prefix: lockedProjectType, gcr_code: '' })));
    }
  }, [isProjectTypeLocked, lockedProjectType]);

  // When operation changes and has workflows, ensure selected gcr_prefix is in allowed list
  useEffect(() => {
    if (selectedOperation && hasWorkflowsAssigned && formData.gcr_prefix && !allowedWorkflows.includes(formData.gcr_prefix)) {
      const firstAllowed = allowedWorkflows[0] || firstWorkflowCode;
      setFormData(prev => ({ ...prev, gcr_prefix: firstAllowed, gcr_code: '' }));
      setProjectCodeEntries(prev => prev.map(e => ({ ...e, gcr_prefix: firstAllowed, gcr_code: '' })));
    }
  }, [selectedOperation, hasWorkflowsAssigned, allowedWorkflows, firstWorkflowCode]);

  // When domain changes: clear selected production line if it's no longer in available list
  useEffect(() => {
    if (selectedProductionLine && availableProductionLines.length > 0 && !availableProductionLines.includes(selectedProductionLine)) {
      setSelectedProductionLine('');
    }
  }, [selectedDomain, availableProductionLines]);

  // When domain or production line changes: clear selected operation if it no longer belongs to filtered list
  useEffect(() => {
    if (selectedOperation) {
      const belongsToFilter = operationsFilteredByDomainAndPL.some(op => op.id === selectedOperation.id);
      if (!belongsToFilter) {
        setSelectedOperation(null);
        setFormData(prev => ({ ...prev, operation: '' }));
        setOperationSearch('');
      }
    }
  }, [selectedDomain, selectedProductionLine, selectedOperation, operationsFilteredByDomainAndPL]);

  // Show warning when operation is selected and inactive
  useEffect(() => {
    if (selectedOperation && !selectedOperation.is_active) {
      showWarning(t('imputations.inactiveOperation'), t('imputations.inactiveOperationMsg'), 5000);
    }
    // Reset quantity to 1 if operation doesn't allow multiple insertions (max_duplicates <= 1)
    const effMax = (selectedOperation?.max_duplicates != null)
      ? Number(selectedOperation.max_duplicates)
      : (selectedOperation?.allows_multiple_insertion ? 100 : 1);
    if (selectedOperation && effMax <= 1) {
      setFormData(prev => ({ ...prev, quantity: 1 }));
    }
  }, [selectedOperation, showWarning]);

  // Sync projectCodeEntries length with quantity when quantity > 1
  const effMaxForEntries = (selectedOperation?.max_duplicates != null)
    ? Math.min(999, Math.max(1, Number(selectedOperation.max_duplicates)))
    : (selectedOperation?.allows_multiple_insertion ? 100 : 1);
  const quantityDisplay = effMaxForEntries > 1 ? (parseInt(formData.quantity, 10) || 1) : 1;
  useEffect(() => {
    if (quantityDisplay <= 1) return;
    const firstAvailable = workflowOptions[0] || firstWorkflowCode;
    const defaultPrefix = isProjectTypeLocked ? lockedProjectType : firstAvailable;
    setProjectCodeEntries(prev => {
      const targetLen = Math.max(1, Math.min(quantityDisplay, 999));
      if (prev.length === targetLen) {
        return isProjectTypeLocked ? prev.map(e => ({ ...e, gcr_prefix: lockedProjectType })) : prev;
      }
      if (prev.length < targetLen) {
        const pad = Array.from({ length: targetLen - prev.length }, () => ({ gcr_prefix: defaultPrefix, gcr_code: '' }));
        return [...prev, ...pad];
      }
      return prev.slice(0, targetLen);
    });
  }, [quantityDisplay, isProjectTypeLocked, lockedProjectType, firstWorkflowCode, workflowOptions]);

  // Show warning when date is in the future
  useEffect(() => {
    if (formData.date_operation) {
      const selectedDate = new Date(formData.date_operation);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // if (selectedDate > today) {
      //   showWarning('Future Date', 'You have selected a future date. Please verify this is correct.', 5000);
      // }
    }
  }, [formData.date_operation, showWarning]);

  // Show warning when status is ECHEC (non-RS tenants only; RS workflow has no failure / 50% rule)
  useEffect(() => {
    if (getTenantSubdomain() !== 'rs' && formData.etat_final === 'ECHEC') {
      showWarning(t('imputations.statusImpact'), t('imputations.statusImpactMsg'), 5000);
    }
  }, [formData.etat_final, showWarning]);

  // Restrict Project Code: digits only when workflow denies alpha; alphanumeric (uppercase) when workflow allows alpha
  const handleProjectCodeChange = (e) => {
    const maxLen = getProjectCodeMaxLength(formData.gcr_prefix);
    const allowAlpha = allowAlphabeticByPrefix[formData.gcr_prefix];
    let filtered = allowAlpha
      ? e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, Math.max(maxLen * 2, 50))
      : e.target.value.replace(/\D/g, '').slice(0, maxLen);
    if (allowAlpha) filtered = filtered.toUpperCase();
    setFormData(prev => ({ ...prev, gcr_code: filtered }));
  };

  // When quantity > 1: update one project code entry
  const setProjectCodeEntry = (index, field, value) => {
    setProjectCodeEntries(prev => {
      const next = [...prev];
      if (!next[index]) next[index] = { gcr_prefix: 'GCR', gcr_code: '' };
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Fetch current user data on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoadingUser(true);
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        setCurrentUser({
          first_name: '',
          last_name: '',
          email: '',
          role: 'EMPLOYE'
        });
      } finally {
        setLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Perform the actual API create (used after validation and optionally after duplicate confirmation)
  // payload.gcr = single gcr (quantity === 1); payload.gcrs = array of gcr (one per imputation when quantity > 1)
  const doCreateImputation = async (payload) => {
    const { formData: fd, quantity, gcr, gcrs } = payload;
    setSaving(true);
    setFormErrors({});
    try {
      const imputationPromises = [];
      const gcrList = Array.isArray(gcrs) && gcrs.length === quantity ? gcrs : Array(quantity).fill(gcr);
      for (let i = 0; i < quantity; i++) {
        const imputationData = {
          date_operation: fd.date_operation,
          gcr: gcrList[i],
          operation_id: parseInt(fd.operation),
          etat_final: fd.etat_final,
          commentaire: fd.commentaire || ''
        };
        imputationPromises.push(api.createImputation(imputationData));
      }
      await Promise.all(imputationPromises);
      const successMessage = quantity > 1
        ? t('imputations.successMany', { count: quantity })
        : t('imputations.successOne')
      showSuccess(t('imputations.entryValidated'), successMessage);
      setTimeout(() => navigate('/employee-dashboard/imputations'), 1500);
    } catch (error) {
      const formattedError = formatApiError(error);
      const data = error?.response?.data;
      const isMaxReached = data && (data.max_reached === true || /maximum.*reached|cannot add more/i.test(data?.error || ''));
      setFormErrors(formattedError.fieldErrors || {});
      const errorMessage = formattedError.generalError || t('imputations.createFailed');
      showError(isMaxReached ? t('imputations.maximumReached') : t('imputations.creationFailed'), errorMessage);
      if (error.response?.status === 429) {
        showWarning(t('imputations.rateLimit'), t('imputations.rateLimitMsg'), 5000);
      } else if (error.response?.status >= 500) {
        showWarning(t('imputations.serverError'), t('imputations.serverErrorMsg'), 5000);
      }
    } finally {
      setSaving(false);
      setPendingSubmitData(null);
    }
  };

  // Handle form submission: validate, check duplicate, then create or show confirmation modal
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setSaving(true);
    setFormErrors({});

    if (!formData.date_operation) {
      setFormErrors({ date_operation: t('imputations.dateRequired') });
      setSaving(false);
      return;
    }
    const effMax = (selectedOperation?.max_duplicates != null)
      ? Math.min(999, Math.max(1, Number(selectedOperation.max_duplicates)))
      : (selectedOperation?.allows_multiple_insertion ? 100 : 1);
    const quantity = effMax > 1 ? (parseInt(formData.quantity, 10) || 1) : 1;
    if (effMax > 1 && (quantity < 1 || quantity > effMax)) {
      setFormErrors({ quantity: t('imputations.quantityRange', { max: effMax }) });
      setSaving(false);
      return;
    }
    if (!formData.operation) {
      setFormErrors({ operation: t('imputations.operationRequired') });
      setSaving(false);
      return;
    }

    // When reference is disabled or no workflows: send gcr "N/A"
    const skipProjectCode = shouldHideWorkflowAndProjectCode;
    if (skipProjectCode) {
      const qty = effMax > 1 ? (parseInt(formData.quantity, 10) || 1) : 1;
      const gcr = 'N/A';
      const gcrs = Array(qty).fill('N/A');
      try {
        await doCreateImputation({ formData: { ...formData }, quantity: qty, gcr, gcrs });
      } catch (err) {
        const formattedError = formatApiError(err);
        setFormErrors(formattedError.fieldErrors || {});
        showError(t('imputations.creationFailed'), formattedError.generalError || t('imputations.createFailedShort'));
      }
      setSaving(false);
      return;
    }

    const buildGcrFromEntry = (entry) => {
      const maxLen = getProjectCodeMaxLength(entry.gcr_prefix);
      const codePrefix = projectCodePrefixByType[entry.gcr_prefix] || '';
      const digits = (entry.gcr_code || '').replace(/\D/g, '');
      const padded = maxLen > 0 ? digits.slice(0, maxLen).padStart(maxLen, '0') : '';
      const fullCode = `${codePrefix}${padded}`;
      return `${entry.gcr_prefix}-${fullCode}`;
    };

    if (quantity === 1) {
      const maxLen = getProjectCodeMaxLength(formData.gcr_prefix);
      const digits = (formData.gcr_code || '').replace(/\D/g, '');
      const isOptional = isProjectCodeOptionalForPrefix(formData.gcr_prefix);
      if (!isOptional && !digits) {
        setFormErrors({ gcr: t('imputations.projectCodeRequired', { count: maxLen }) });
        setSaving(false);
        return;
      }
      const padded = maxLen > 0 ? digits.slice(0, maxLen).padStart(maxLen, '0') : '';
      if (!isOptional && padded === getZeroProjectCode(formData.gcr_prefix)) {
        setFormErrors({ gcr: t('imputations.projectCodeAllZeros') });
        setSaving(false);
        return;
      }
      const codePrefix = projectCodePrefixByType[formData.gcr_prefix] || '';
      const fullCode = `${codePrefix}${padded}`;
      const gcr = `${formData.gcr_prefix}-${fullCode}`;

      try {
        const opId = formData.operation ? parseInt(formData.operation, 10) : null;
        const check = await checkImputationDuplicate(formData.date_operation, gcr, opId);
        const { duplicate, max_reached, current_count = 0, max_allowed } = check;

        if (max_reached) {
          const cap = max_allowed ?? effMax;
          showError(
            'Maximum reached',
            `You have reached the maximum allowed imputations (${cap}) for this operation and project code on this date. You cannot add more.`
          );
          setSaving(false);
          return;
        }

        const remaining = Math.max(0, (max_allowed ?? effMax) - (current_count ?? 0));
        const toCreate = Math.min(1, remaining);
        if (toCreate <= 0) {
          showError(t('imputations.maximumReached'), t('imputations.cannotAddMore'));
          setSaving(false);
          return;
        }

        if (duplicate) {
          setPendingSubmitData({ formData: { ...formData }, quantity: toCreate, gcr });
          setShowDuplicateModal(true);
          setSaving(false);
          return;
        }
        await doCreateImputation({ formData: { ...formData }, quantity: toCreate, gcr });
      } catch (error) {
        const formattedError = formatApiError(error);
        const data = error?.response?.data;
        const isMaxReached = data && (data.max_reached === true || /maximum.*reached|cannot add more/i.test(data.error || ''));
        setFormErrors(formattedError.fieldErrors || {});
        showError(
          'Error',
          isMaxReached ? (data?.error || formattedError.generalError) : (formattedError.generalError || t('imputations.duplicateCheckFailed'))
        );
        setSaving(false);
      }
      return;
    }

    // quantity > 1: validate each project code entry and build gcrs array
    const gcrs = [];
    for (let i = 0; i < quantity; i++) {
      const entry = projectCodeEntries[i] || { gcr_prefix: 'GCR', gcr_code: '' };
      const maxLen = getProjectCodeMaxLength(entry.gcr_prefix);
      const digits = (entry.gcr_code || '').replace(/\D/g, '');
      const isOptional = isProjectCodeOptionalForPrefix(entry.gcr_prefix);
      if (!isOptional && !digits) {
        setFormErrors({ gcr: t('imputations.projectCodeRequiredFor', { number: i + 1, count: maxLen }) });
        setSaving(false);
        return;
      }
      const padded = maxLen > 0 ? digits.slice(0, maxLen).padStart(maxLen, '0') : '';
      if (!isOptional && padded === getZeroProjectCode(entry.gcr_prefix)) {
        setFormErrors({ gcr: t('imputations.projectCodeAllZerosFor', { number: i + 1 }) });
        setSaving(false);
        return;
      }
      gcrs.push(buildGcrFromEntry(entry));
    }

    try {
      await doCreateImputation({ formData: { ...formData }, quantity, gcrs });
    } catch (error) {
      const formattedError = formatApiError(error);
      const data = error?.response?.data;
      const isMaxReached = data && (data.max_reached === true || /maximum.*reached|cannot add more/i.test(data?.error || ''));
      setFormErrors(formattedError.fieldErrors || {});
      showError(
        isMaxReached ? t('imputations.maximumReached') : t('imputations.creationFailed'),
        formattedError.generalError || t('imputations.createFailed')
      );
      setSaving(false);
    }
  };

  const handleDuplicateModalConfirm = () => {
    setShowDuplicateModal(false);
    if (pendingSubmitData) {
      doCreateImputation(pendingSubmitData);
    }
  };

  const handleDuplicateModalCancel = () => {
    setShowDuplicateModal(false);
    setPendingSubmitData(null);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      <AdminTopBar
        title={t('imputations.createTitle')}
        showSearch={false}
        showMenuButton={false}
      />
      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-background relative">
        <div className="flex-1 overflow-y-auto p-8 scroll-smooth">
          <div className="max-w-4xl mx-auto flex flex-col gap-6">
            {/* Page Heading */}
            <div className="flex flex-wrap justify-between items-end gap-4 px-1">
              <div className="flex min-w-72 flex-col gap-2">
                <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
                  {t('imputations.createTitle')}
                </h1>
                <p className="text-slate-500 text-base font-normal leading-normal">
                  {t('imputations.createSubtitle')}
                </p>
              </div>
              <button
                onClick={() => navigate('/employee-dashboard/imputations')}
                className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 text-slate-700 hover:bg-slate-100 text-sm font-medium transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                <span>{t('imputations.backToList')}</span>
              </button>
            </div>

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />

      {/* Duplicate project code confirmation modal */}
      <AnimatePresence>
        {showDuplicateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => {}}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="material-symbols-outlined text-amber-500 text-3xl">info</span>
                <h3 className="text-lg font-semibold text-slate-900">{t('imputations.duplicateTitle')}</h3>
              </div>
              <p className="text-slate-600 text-sm mb-6">
                {t('imputations.duplicateBody')}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleDuplicateModalCancel}
                  className="px-5 py-2.5 rounded-lg text-slate-700 bg-slate-100 hover:bg-slate-200 font-medium transition-colors"
                >
                  {tCommon('cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleDuplicateModalConfirm}
                  className="px-5 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white font-medium transition-colors"
                >
                  {t('imputations.continueAnyway')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6 md:p-8">
          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            {/* Error Messages */}
            {Object.keys(formErrors).length > 0 && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                {Object.values(formErrors).flat().join(', ')}
              </div>
            )}

            {/* Row 1: Date */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-900">{t('imputations.dateOfActivity')}</label>
              <div className="relative flex items-center group">
                <input
                  type="date"
                  value={formData.date_operation}
                  onChange={(e) => setFormData({ ...formData, date_operation: e.target.value })}
                  required
                  className={`w-full h-12 rounded-lg border ${
                    formErrors.date_operation ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                  } px-4 text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none`}
                />
                <div className="absolute right-4 text-slate-500 pointer-events-none group-focus-within:text-primary transition-colors">
                  <span className="material-symbols-outlined">calendar_today</span>
                </div>
              </div>
              {formErrors.date_operation && (
                <p className="text-red-600 text-xs mt-1">{formErrors.date_operation[0]}</p>
              )}
            </div>

            {/* Row 2: Domain (filters Operation Code) */}
            {availableDomains.length > 0 && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900">{t('imputations.domain')}</label>
                <div className="relative flex flex-wrap gap-2 w-full rounded-lg bg-slate-50 p-1 border border-slate-200">
                  {['', ...availableDomains].map((domain) => (
                    <label key={domain || '__all__'} className="flex-1 min-w-[100px] h-10 relative cursor-pointer whitespace-nowrap">
                      <input
                        type="radio"
                        name="domain"
                        value={domain}
                        checked={selectedDomain === domain}
                        onChange={() => setSelectedDomain(domain)}
                        className="peer sr-only"
                      />
                      <div className="relative flex h-full w-full items-center justify-center rounded-md text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary whitespace-nowrap">
                        {selectedDomain === domain && (
                          <motion.div
                            layoutId="domainIndicator"
                            className="absolute inset-0 rounded-md bg-white shadow-sm"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10 whitespace-nowrap">{domain || t('imputations.allDomains')}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">{t('imputations.filterByDomain')}</p>
              </div>
            )}

            {/* Row 2.5: PL (Production Line) filter */}
            {availableProductionLines.length > 0 && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900">{t('imputations.plFilter')}</label>
                <div className="relative flex flex-wrap gap-2 w-full rounded-lg bg-slate-50 p-1 border border-slate-200">
                  {['', ...availableProductionLines].map((pl) => (
                    <label key={pl || '__all__'} className="flex-1 min-w-[100px] h-10 relative cursor-pointer whitespace-nowrap">
                      <input
                        type="radio"
                        name="production_line"
                        value={pl}
                        checked={selectedProductionLine === pl}
                        onChange={() => setSelectedProductionLine(pl)}
                        className="peer sr-only"
                      />
                      <div className="relative flex h-full w-full items-center justify-center rounded-md text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary whitespace-nowrap">
                        {selectedProductionLine === pl && (
                          <motion.div
                            layoutId="plIndicator"
                            className="absolute inset-0 rounded-md bg-white shadow-sm"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10 whitespace-nowrap">{pl || t('imputations.allPL')}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">{t('imputations.filterByPL')}</p>
              </div>
            )}

            {/* Row 3: Operation Search */}
            <div className="flex flex-col gap-2 relative" ref={operationRef}>
              <label className="text-sm font-semibold text-slate-900">{t('imputations.operationCode')}</label>
              <div className="relative">
                <div className={`flex items-center w-full h-12 rounded-lg border ${
                  formErrors.operation ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100'
                } px-4 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all overflow-hidden`}>
                  <span className="material-symbols-outlined text-slate-500 mr-3">search</span>
                  <input
                    type="text"
                    value={operationSearch}
                    onChange={(e) => {
                      setOperationSearch(e.target.value);
                      setShowOperationDropdown(true);
                    }}
                    onFocus={() => setShowOperationDropdown(true)}
                    placeholder={t('imputations.searchCodeOrDescription')}
                    className="w-full h-full bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400"
                  />
                  {operationSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setOperationSearch('');
                        setSelectedOperation(null);
                        setFormData({ ...formData, operation: '' });
                      }}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                  )}
                </div>
                
                {showOperationDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {getFilteredOperations(operationSearch).map(op => (
                      <div
                        key={op.id}
                        onClick={() => {
                          setFormData({ ...formData, operation: op.id.toString() });
                          setOperationSearch(`${op.production_line_lp} · ${op.activity}`);
                          setSelectedOperation(op);
                          setShowOperationDropdown(false);
                        }}
                        className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900">{op.production_line_lp} · {op.activity}</div>
                        <div className="text-xs text-slate-500 mt-1">{t('imputations.standardTime')}: {op.temps_standard}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedOperation && (
                <p className="text-xs text-slate-500 mt-1.5 pl-1">
                  {t('imputations.selectedOp', { activity: selectedOperation.activity, status: selectedOperation.is_active ? tCommon('active') : tCommon('inactive') })}
                </p>
              )}
              {formErrors.operation && (
                <p className="text-red-600 text-xs mt-1">{formErrors.operation[0]}</p>
              )}
            </div>

            {/* Row 2.5: Quantity (only if operation allows multiple / max_duplicates > 1) */}
            {(() => {
              const effMax = (selectedOperation?.max_duplicates != null)
                ? Math.min(999, Math.max(1, Number(selectedOperation.max_duplicates)))
                : (selectedOperation?.allows_multiple_insertion ? 100 : 1);
              return effMax > 1 && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-900">
                    {t('imputations.quantity')} <span className="text-slate-500 font-normal text-xs">{t('imputations.quantityHint')}</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={effMax}
                    value={formData.quantity}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 1;
                        if (value >= 1 && value <= effMax) {
                        setFormData(prev => ({ ...prev, quantity: value }));
                      }
                    }}
                    className={`w-full h-12 rounded-lg border px-4 text-slate-900 focus:ring-1 focus:ring-primary transition-all outline-none ${
                      formErrors.quantity ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 focus:border-red-500' : 'border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 focus:border-primary'
                    }`}
                    placeholder="1"
                  />
                  <p className="text-xs text-slate-500">
                    {t('imputations.quantityHelp', { max: effMax })}
                  </p>
                  {formErrors.quantity && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.quantity}</p>
                  )}
                </div>
              );
            })()}

            {/* Row 3: Status & Time Logic */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Final Status */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900">{t('imputations.executionStatus')}</label>
                <div className="relative w-full">
                  <select
                    value={formData.etat_final}
                    onChange={(e) => setFormData({ ...formData, etat_final: e.target.value })}
                    required
                    className="appearance-none w-full h-12 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none cursor-pointer"
                  >
                    {etatChoices.map(choice => (
                      <option key={choice.value} value={choice.value}>{choice.label}</option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none flex items-center">
                    <span className="material-symbols-outlined">expand_more</span>
                  </div>
                </div>
              </div>

              {/* Standard Time (Auto-calculated) */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  {t('imputations.standardTimeImpact')}
                  <span className="material-symbols-outlined text-[16px] text-slate-500 cursor-help" title={t('imputations.calculatedBasedOnStatus')}>info</span>
                </label>
                <div className="flex items-center w-full h-12 rounded-lg bg-slate-50 px-4 border border-transparent">
                  <span className={`font-mono font-bold tracking-wider ${
                    getTenantSubdomain() !== 'rs' && formData.etat_final === 'ECHEC' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {getTenantSubdomain() !== 'rs' && formData.etat_final === 'ECHEC' ? '50%' : '100%'}
                  </span>
                  <span className="ml-auto text-xs text-slate-500 uppercase font-medium tracking-wide">{t('imputations.calculated')}</span>
                </div>
              </div>
            </div>

            {/* Row 4: Workflow & Project Code — hidden when reference disabled or no workflows */}
            {shouldHideWorkflowAndProjectCode ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-600">
                  {t('imputations.referenceNotUsed')}
                </p>
              </div>
            ) : quantityDisplay === 1 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-900">
                    {t('imputations.workflow')}
                    {isProjectTypeLocked && (
                      <span className="ml-2 text-xs font-normal text-slate-500">{t('imputations.fixedForOperation')}</span>
                    )}
                  </label>
                  <div className={`relative grid h-12 w-full rounded-lg bg-slate-50 p-1 border border-slate-200 ${isProjectTypeLocked ? 'opacity-80 pointer-events-none' : ''}`}
                    style={{ gridTemplateColumns: `repeat(${workflowOptions.length}, minmax(0, 1fr))` }}>
                    {workflowOptions.map((prefix) => (
                      <label key={prefix} className={`flex-1 h-full relative ${isProjectTypeLocked ? 'cursor-default' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="gcr_prefix"
                          value={prefix}
                          checked={formData.gcr_prefix === prefix}
                          onChange={(e) => !isProjectTypeLocked && setFormData(prev => ({ ...prev, gcr_prefix: e.target.value, gcr_code: '' }))}
                          disabled={isProjectTypeLocked}
                          className="peer sr-only"
                        />
                        <div className="relative flex h-full w-full items-center justify-center rounded-md text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary">
                          {formData.gcr_prefix === prefix && (
                            <motion.div
                              layoutId="projectTypeIndicator"
                              className="absolute inset-0 rounded-md bg-white shadow-sm"
                              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                            />
                          )}
                          <span className="relative z-10">{workflowLabelByPrefix[prefix] ?? prefix}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                {!isProjectCodeOptionalForPrefix(formData.gcr_prefix) && (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-900">
                      {t('imputations.projectCode')} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-1 focus-within:ring-primary focus-within:border-primary">
                      <span className="inline-flex items-center h-12 px-4 bg-slate-100 text-slate-700 font-mono text-sm border-r border-slate-200 shrink-0">
                        {projectCodePrefixByType[formData.gcr_prefix] || ''}
                      </span>
                      <input
                        type="text"
                        inputMode={allowAlphabeticByPrefix[formData.gcr_prefix] ? 'text' : 'numeric'}
                        value={formData.gcr_code}
                        onChange={handleProjectCodeChange}
                        required
                        maxLength={allowAlphabeticByPrefix[formData.gcr_prefix] ? Math.max(getProjectCodeMaxLength(formData.gcr_prefix) * 2, 50) : getProjectCodeMaxLength(formData.gcr_prefix)}
                        placeholder={allowAlphabeticByPrefix[formData.gcr_prefix]
                          ? 'e.g. 123 or ABC123'
                          : (formData.gcr_prefix === 'BYNOW' ? 'e.g. 1234567890' : `Up to ${getProjectCodeMaxLength(formData.gcr_prefix)} digits`)}
                        className={`flex-1 h-12 px-4 text-slate-900 font-mono placeholder-slate-400 outline-none ${
                          formErrors.gcr ? 'bg-red-50' : ''
                        }`}
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      {t('imputations.projectCodeHint', { count: getProjectCodeMaxLength(formData.gcr_prefix) })}
                      {allowAlphabeticByPrefix[formData.gcr_prefix] ? t('imputations.projectCodeLettersAllowed') : t('imputations.projectCodeDigitsOnly')}
                    </p>
                    {formErrors.gcr && (
                      <p className="text-red-600 text-xs mt-1">{formErrors.gcr[0]}</p>
                    )}
                  </div>
                )}
              </div>
            ) : quantityDisplay > 1 ? (
                <div className="flex flex-col gap-4">
                <p className="text-sm font-semibold text-slate-900">{t('imputations.workflowAndCodePerImputation')}</p>
                <p className="text-xs text-slate-500">
                  {isProjectTypeLocked ? t('imputations.workflowFixedHint', { type: lockedProjectType }) : t('imputations.setWorkflowAndCode', { count: quantityDisplay })}
                </p>
                <div className="flex flex-col gap-6">
                  {Array.from({ length: quantityDisplay }, (_, i) => {
                    const entry = projectCodeEntries[i] || { gcr_prefix: lockedProjectType, gcr_code: '' };
                    return (
                      <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-4">
                        <span className="text-sm font-medium text-slate-700">{t('imputations.imputationNumber', { number: i + 1 })}</span>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="text-xs font-semibold text-slate-700">{t('imputations.workflow')}</label>
                            <div className={`relative grid h-10 w-full rounded-lg bg-slate-50 p-1 border border-slate-200 ${isProjectTypeLocked ? 'opacity-80 pointer-events-none' : ''}`}
                              style={{ gridTemplateColumns: `repeat(${workflowOptions.length}, minmax(0, 1fr))` }}>
                              {workflowOptions.map((prefix) => (
                                <label key={`${i}-${prefix}`} className={`flex-1 h-full relative ${isProjectTypeLocked ? 'cursor-default' : 'cursor-pointer'}`}>
                                  <input
                                    type="radio"
                                    name={`gcr_prefix_${i}`}
                                    value={prefix}
                                    checked={entry.gcr_prefix === prefix}
                                    onChange={() => !isProjectTypeLocked && setProjectCodeEntries(prev => {
                                  const next = [...prev];
                                  if (!next[i]) next[i] = { gcr_prefix: 'GCR', gcr_code: '' };
                                  next[i] = { ...next[i], gcr_prefix: prefix, gcr_code: '' };
                                  return next;
                                })}
                                    disabled={isProjectTypeLocked}
                                    className="peer sr-only"
                                  />
                                  <div className="relative flex h-full w-full items-center justify-center rounded text-sm font-medium text-slate-500 transition-colors peer-checked:text-primary peer-checked:bg-white peer-checked:shadow-sm">
                                    <span className="relative z-10">{workflowLabelByPrefix[prefix] ?? prefix}</span>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          {!isProjectCodeOptionalForPrefix(entry.gcr_prefix) && (
                            <div className="flex flex-col gap-2">
                              <label className="text-xs font-semibold text-slate-700">
                                {t('imputations.projectCode')} <span className="text-red-500">*</span>
                              </label>
                              <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-1 focus-within:ring-primary">
                                <span className="inline-flex items-center h-10 px-3 bg-slate-100 text-slate-700 font-mono text-xs border-r border-slate-200 shrink-0">
                                  {projectCodePrefixByType[entry.gcr_prefix] || ''}
                                </span>
                                <input
                                  type="text"
                                  inputMode={allowAlphabeticByPrefix[entry.gcr_prefix] ? 'text' : 'numeric'}
                                  value={entry.gcr_code}
                                  onChange={(e) => {
                                    const maxLen = getProjectCodeMaxLength(entry.gcr_prefix);
                                    const allowAlpha = allowAlphabeticByPrefix[entry.gcr_prefix];
                                    let filtered = allowAlpha
                                      ? e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, Math.max(maxLen * 2, 50))
                                      : e.target.value.replace(/\D/g, '').slice(0, maxLen);
                                    if (allowAlpha) filtered = filtered.toUpperCase();
                                    setProjectCodeEntry(i, 'gcr_code', filtered);
                                  }}
                                  required
                                  maxLength={allowAlphabeticByPrefix[entry.gcr_prefix] ? Math.max(getProjectCodeMaxLength(entry.gcr_prefix) * 2, 50) : getProjectCodeMaxLength(entry.gcr_prefix)}
                                  placeholder={allowAlphabeticByPrefix[entry.gcr_prefix] ? t('imputations.projectCodePlaceholderAlpha') : t('imputations.projectCodePlaceholderDigits')}
                                  className="flex-1 h-10 px-3 text-slate-900 font-mono text-sm placeholder-slate-400 outline-none"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {formErrors.gcr && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.gcr}</p>
                )}
              </div>
            ) : null}

            {/* Row 5: Comments */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-900 flex justify-between">
                {t('imputations.comments')}
                <span className="text-xs font-normal text-slate-500">{tCommon('optional')}</span>
              </label>
              <textarea
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                rows={4}
                placeholder={t('imputations.commentPlaceholderDetail')}
                className="w-full min-h-[100px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-4 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder-slate-400 focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none resize-y"
              />
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-slate-200"></div>

            {/* Row 6: Actions */}
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/employee-dashboard/imputations')}
                className="px-6 py-2.5 rounded-lg text-slate-500 hover:text-slate-700 font-medium transition-colors"
              >
                {tCommon('cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="group relative px-6 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white font-medium shadow-md shadow-blue-500/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center gap-2">
                  {saving ? t('imputations.creating') : t('imputations.validateEntry')}
                  {!saving && (
                    <span className="material-symbols-outlined text-[18px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateImputationPage;

